import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { EntryModalComponent } from '../task-timeline/entry-modal/entry-modal';
import { TimeEntriesService } from '../../../shared/services/time-entries.service';
import { TauriService } from '../../../shared/services/tauri.service';

/**
 * Full-page host for time entry creation, replacing the modal. The selected slot
 * travels in the URL as `start`/`end`, so the page survives a reload and can be
 * linked to; the form component itself is unchanged apart from its shell classes.
 */
@Component({
  selector: 'app-time-entry-create-page',
  imports: [CommonModule, EntryModalComponent],
  templateUrl: './time-entry-create-page.html',
})
export class TimeEntryCreatePageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private timeEntriesService = inject(TimeEntriesService);
  private tauriService = inject(TauriService);

  startTime?: Date;
  endTime?: Date;
  /** Set when the URL carries no usable slot — the form needs both bounds. */
  invalidRange = false;

  ngOnInit() {
    const params = this.route.snapshot.queryParamMap;
    const start = this.parseDate(params.get('start'));
    const end = this.parseDate(params.get('end'));

    if (!start || !end || end <= start) {
      this.invalidRange = true;
      return;
    }
    this.startTime = start;
    this.endTime = end;

    // The form reads Tauri activities from timelineTasks$ to draft descriptions;
    // make sure they are the ones for the selected day. task-timeline used to do
    // this before opening the modal.
    const day = new Date(start);
    day.setHours(0, 0, 0, 0);
    this.tauriService.getTasksForDate(day);
  }

  private parseDate(raw: string | null): Date | null {
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  async onSave(data: any) {
    await this.timeEntriesService.createFromEntryPayload(data, this.startTime!, this.endTime!);
    this.goToActivity();
  }

  /**
   * Back to the timeline on the day the slot belongs to. Without the date the
   * timeline falls back to today, which means catching up on a backlog sends you
   * back to today after every single entry.
   */
  goToActivity() {
    const day = this.startTime;
    this.router.navigate(['/activity'], {
      queryParams: day ? { date: this.toLocalDateString(day) } : {},
    });
  }

  /** Local calendar day, not UTC — same convention as the timeline's own state. */
  private toLocalDateString(d: Date): string {
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  }
}
